import React, { createContext, useContext, useEffect, useState } from 'react'
import { connectSocket, disconnectSocket, updateSocketAuthToken } from './socketClient'
import { useRealtimeSync } from './useRealtimeSync'
import { getAccessToken } from '../utils/tokenStorage'
import { useAuthStore } from '../../features/auth/hooks/useAuthStore'

interface RealtimeContextValue {
  isConnected: boolean
  lastEvent: { event: string; timestamp: Date; payload?: unknown } | null
}

const RealtimeContext = createContext<RealtimeContextValue>({
  isConnected: false,
  lastEvent: null,
})

export const useRealtime = () => useContext(RealtimeContext)

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const storeToken = useAuthStore((state) => state.token || state.accessToken)
  const [tokenPresent, setTokenPresent] = useState(() => Boolean(getAccessToken() || storeToken))
  const { isConnected, lastEvent } = useRealtimeSync()

  // Track authStore token changes
  useEffect(() => {
    const activeToken = storeToken || getAccessToken()
    if (activeToken) {
      setTokenPresent(true)
      updateSocketAuthToken(activeToken)
    } else {
      setTokenPresent(false)
      disconnectSocket()
    }
  }, [storeToken])

  // Track token existence in localStorage (handles multi-tab login / logout)
  useEffect(() => {
    const checkToken = () => {
      const activeToken = getAccessToken() || useAuthStore.getState().token || useAuthStore.getState().accessToken
      const hasToken = Boolean(activeToken)
      setTokenPresent(hasToken)
      if (hasToken && activeToken) {
        updateSocketAuthToken(activeToken)
      } else {
        disconnectSocket()
      }
    }

    checkToken()

    window.addEventListener('storage', checkToken)
    return () => {
      window.removeEventListener('storage', checkToken)
    }
  }, [])

  // Connect on initial load if token exists
  useEffect(() => {
    if (tokenPresent) {
      connectSocket()
    }
  }, [tokenPresent])

  return (
    <RealtimeContext.Provider value={{ isConnected, lastEvent }}>
      {children}
    </RealtimeContext.Provider>
  )
}
